import { FC, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import LoadingPanel from "../../../custom-components/CustomLoader";
import { NullableCollection, ResponseMessageObj, defaultResponseMessageObj } from "../../../types/ApiResults";
import { DEFAULT_PAGE } from "../../../domain/constants";
import { PaginationEnum } from "../../../domain/enums/common/PaginationEnum";
import { ResponseMessage } from "../../../domain/enums/common/ResponseMessageEnum";
import Toast from "../../../custom-components/Toast";
import Pagination from "../../../custom-components/CustomPagination";
import SystemEventsTable from "./SystemEventsTable";
import SystemEventsHeader from "./SystemEventsHeader";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { SelectValue } from "../../../types/SelectProps";
import { useTranslation } from "react-i18next";
import { SystemEvent } from "../../../domain/models/SystemEvent";
import { CollectionResponse } from "../../../types/ApiResults";
import { systemEventServiceFactory } from "../../../services/SystemEventServiceImpl";
import { SystemEventsSearchFiltersDTO } from "../../../validation-schemas/SystemEventsSearchFiltersFields";
import {
    systemEventsSearchFiltersFields,
    systemEventsSearchFiltersSchemaFactory,
} from "../../../validation-schemas/SystemEventsSearchFiltersFields";
import { useAuth } from "../../../context/AuthProvider";

const systemEventService = systemEventServiceFactory();

const SystemEventsPage: FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [systemEvents, setSystemEvents] = useState<CollectionResponse<SystemEvent>>({
        rows: [],
        totalRows: 0,
        totalCount: 0,
        totalPages: 0,
        currentPage: DEFAULT_PAGE,
        limit: PaginationEnum.SHOW_20,
    });

    const [reloadPage, setReloadPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
    const [currentLimit, setCurrentLimit] = useState(PaginationEnum.SHOW_20);
    const [messageObj, setMessageObj] = useState<ResponseMessageObj>(defaultResponseMessageObj);
    const [selectedSystemEvent, setSelectedSystemEvent] = useState<SystemEvent | null>(null);

    const getSystemEvents = async (filterValues?: SystemEventsSearchFiltersDTO) => {
        try {
            setLoading(true);
            const systemEventsResult = await systemEventService.getAll(
                currentPage,
                currentLimit,
                filterValues
            );

            if (systemEventsResult) {
                setSystemEvents(systemEventsResult);
                setReloadPage(false);
                setMessageObj({
                    id: uuidv4(),
                    messageType: ResponseMessage.SUCCESS,
                    messageKey: "messages.systemEventsReceived",
                });
            } else {
                setMessageObj({
                    id: uuidv4(),
                    messageType: ResponseMessage.DANGER,
                    messageKey: "errors.common",
                });
            }
        } catch (error: any) {
            console.error('Error fetching system events:', error);
            const errorMessage = error?.message || error?.toString() || 'Unknown error';
            console.error('Error message:', errorMessage);
            setMessageObj({
                id: uuidv4(),
                messageType: ResponseMessage.DANGER,
                messageKey: "errors.common",
            });
            setSystemEvents({
                rows: [],
                totalRows: 0,
                totalCount: 0,
                totalPages: 0,
                currentPage: currentPage,
                limit: currentLimit,
            });
        } finally {
            setLoading(false);
        }
    };

    const [searchFiltersFields, setSearchFiltersFields] = useState<SystemEventsSearchFiltersDTO>({} as SystemEventsSearchFiltersDTO);
    const searchFiltersForm = useCustomFormik<SystemEventsSearchFiltersDTO>({
        initial: null,
        fields: systemEventsSearchFiltersFields,
        schema: systemEventsSearchFiltersSchemaFactory(t),
        onSubmit: async (values) => {
            setSearchFiltersFields(values);
            setCurrentPage(DEFAULT_PAGE);
            await getSystemEvents(values);
        },
    });

    useEffect(() => {
        if (user) {
            getSystemEvents(searchFiltersFields);
        } else {
            console.log('User not authenticated, skipping system events fetch');
        }
    }, [currentLimit, currentPage, reloadPage]);

    const limitHandler = (value: SelectValue) => {
        setCurrentLimit(value as number);
        setCurrentPage(DEFAULT_PAGE);
    };

    const pageHandler = (value: number) => {
        setCurrentPage(value);
    };

    const resetMessageObj = () => {
        setMessageObj(defaultResponseMessageObj);
    };

    const resetFiltersHandler = () => {
        searchFiltersForm.form.resetForm();
        setSearchFiltersFields({} as SystemEventsSearchFiltersDTO);
        setReloadPage(true);
    };

    const resetSelectedSystemEvent = () => {
        setSelectedSystemEvent(null);
    };

    // if (!user) {
    //     return (
    //         <div className="relative z-0 bg-light-bg-700 dark:bg-dark-bg-800">
    //             <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
    //                 <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
    //                     {t("console.sidebar.systemEventsPage")}
    //                 </h1>
    //                 <div className="text-center py-12">
    //                     <div className="text-gray-500 dark:text-gray-400 text-lg">
    //                         Будь ласка, увійдіть в систему для перегляду системних подій
    //                     </div>
    //                 </div>
    //             </div>
    //         </div>
    //     );
    // }

    return (
        <div className="relative z-0 bg-light-bg-700 dark:bg-dark-bg-800">
            <LoadingPanel isVisible={loading} />
            <SystemEventsHeader
                searchFiltersForm={searchFiltersForm.form}
                filtersFields={searchFiltersFields}
                limitHandler={limitHandler}
                resetFiltersHandler={resetFiltersHandler}
                currentLimit={currentLimit}
            />
            <SystemEventsTable
                systemEvents={systemEvents.rows}
                updateHandler={setSelectedSystemEvent}
            />
            <Pagination currentPage={currentPage} totalPages={systemEvents.totalPages} pageHandler={pageHandler} />
            <Toast messageObj={messageObj} resetMessageObj={resetMessageObj} />
        </div>
    );
};

export default SystemEventsPage;
